/* eslint-disable @next/next/no-img-element */
"use client";
import { Dialog, DialogContent, DialogOverlay } from "@/components/ui/dialog";
import { University } from "@/store/types/universityTypes";
import { X } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { fetchUniversityGalleryByCollageName } from "../common/GetCollageGallery";

export default function Gallery({ data }: { data: University }) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [universityLogos, setUniversityLogos] = useState<string[]>([]);

  useEffect(() => {
    if (data?.addressInfo?.country) {
      fetchUniversityGalleryByCollageName(
        data?.addressInfo?.country,
        setUniversityLogos,
        data?.name?.replace(/\s+/g, "_").toLowerCase(),
      );
    }
  }, [data?.addressInfo?.country, data?.name]);

  return (
    <div className="pt-[32px] sm:pt-[40px] md:pt-[46px] lg:pt-[56px]">
      <h2 className="text-xl sm:text-2xl font-semibold mb-4">
        University Gallery
      </h2>

      {universityLogos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-[400px]">
          {universityLogos.slice(0, 5).map((image, index) => (
            <div
              key={index}
              className={`relative rounded-lg overflow-hidden cursor-pointer ${
                index === 0 ? "col-span-2 row-span-2" : ""
              }`}
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image}
                alt={image}
                className="hover:scale-105 transition-transform duration-300 object-cover w-full h-full"
                onError={(e) => {
                  // Hide this image element if it fails to load
                  const target = e.target as HTMLImageElement;
                  if (target.parentElement) {
                    target.parentElement.style.display = "none";
                  }
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          No gallery images available for this university.
        </p>
      )}

      <Dialog
        open={!!selectedImage}
        onOpenChange={(open) => {
          if (!open) setSelectedImage(null);
        }}
      >
        <DialogOverlay className="bg-black/80" />
        <DialogContent className="sm:max-w-4xl border-none bg-transparent shadow-none p-0 flex items-center justify-center">
          <button
            onClick={() => setSelectedImage(null)}
            aria-label="Close image preview"
            className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 z-10"
          >
            <X size={24} />
          </button>

          <div className="relative w-full h-[80vh] mt-10">
            {selectedImage && (
              <Image
                src={selectedImage}
                alt="University image view"
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 90vw"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
